import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild, computed, signal } from '@angular/core';

import { animacaoSurgir } from '../../../utils/chat-animacoes.utils';
import { MethodsUtils } from '../../../../shared/util/methods.utils';
import { VELOCIDADES_MIDIA } from '../../../utils/chat.constantes';
import { CHAVE_VELOCIDADE_AUDIO, QTD_BARRAS_PLAYER_AUDIO } from './utils/player-audio.constantes';

@Component({
  selector: 'app-player-audio',
  standalone: true,
  templateUrl: './player-audio.component.html',
  styleUrl: './player-audio.component.css',
  animations: [animacaoSurgir]
})
export class PlayerAudioComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) src = '';
  @Input() cliente = false;
  @Output() terminou = new EventEmitter<void>();

  @ViewChild('audio', { static: true }) audioRef!: ElementRef<HTMLAudioElement>;

  tocando = signal(false);
  carregando = signal(false);
  duracao = signal(0);
  atual = signal(0);
  velocidade = signal(MethodsUtils.lerVelocidadeMidiaSalva(CHAVE_VELOCIDADE_AUDIO));
  arrastando = signal(false);
  barras: number[] = [];

  readonly progresso = computed(() => (this.duracao() > 0 ? Math.min(1, this.atual() / this.duracao()) : 0));
  readonly tempoExibido = computed(() =>
    MethodsUtils.formatarTempoMidia(this.tocando() || this.arrastando() || this.atual() > 0 ? this.atual() : this.duracao()));

  private corrigindoDuracao = false;
  private quadro: number | null = null;

  ngOnChanges(): void {
    this.barras = this.gerarBarras(this.src);
    this.pausar();
    this.atual.set(0);
  }

  ngOnDestroy(): void {
    this.pausar();
    this.pararAcompanhamento();
  }

  private acompanharReproducao(): void {
    this.pararAcompanhamento();
    const passo = () => {
      const audio = this.audioRef?.nativeElement;
      if (!audio || audio.paused) {
        this.quadro = null;
        return;
      }
      if (!this.arrastando() && !this.corrigindoDuracao) this.atual.set(audio.currentTime);
      this.quadro = requestAnimationFrame(passo);
    };
    this.quadro = requestAnimationFrame(passo);
  }

  acompanharReproducaoPublico(): void {
    this.tocando.set(true);
    this.acompanharReproducao();
  }

  private pararAcompanhamento(): void {
    if (this.quadro !== null) cancelAnimationFrame(this.quadro);
    this.quadro = null;
  }

  alternar(event: Event): void {
    event.stopPropagation();
    if (this.tocando()) {
      this.pausar();
      return;
    }
    this.tocar();
  }

  tocar(): void {
    const audio = this.audioRef.nativeElement;
    document.querySelectorAll('audio').forEach(outro => { if (outro !== audio) outro.pause(); });
    this.carregando.set(true);    this.velocidade.set(MethodsUtils.lerVelocidadeMidiaSalva(CHAVE_VELOCIDADE_AUDIO));
    audio.playbackRate = this.velocidade();
    audio.play()
      .then(() => {
        this.tocando.set(true);
        this.acompanharReproducao();
      })
      .catch(() => this.tocando.set(false))
      .finally(() => this.carregando.set(false));
  }

  trocarVelocidade(event: Event): void {
    event.stopPropagation();
    const proxima = VELOCIDADES_MIDIA[(VELOCIDADES_MIDIA.indexOf(this.velocidade()) + 1) % VELOCIDADES_MIDIA.length];
    this.velocidade.set(proxima);
    this.audioRef.nativeElement.playbackRate = proxima;
    MethodsUtils.salvarStorage(CHAVE_VELOCIDADE_AUDIO, String(proxima));
  }

  aoPressionarOnda(event: PointerEvent): void {
    event.stopPropagation();
    if (!this.duracao()) return;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.arrastando.set(true);
    this.irParaPonto(event);
  }

  aoMoverOnda(event: PointerEvent): void {
    if (this.arrastando()) this.irParaPonto(event);
  }

  aoSoltarOnda(event: PointerEvent): void {
    if (!this.arrastando()) return;
    this.irParaPonto(event);
    this.arrastando.set(false);
  }

  aoTeclarOnda(event: KeyboardEvent): void {
    if (!this.duracao() || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    event.preventDefault();
    const audio = this.audioRef.nativeElement;
    const passo = event.key === 'ArrowRight' ? 5 : -5;
    audio.currentTime = Math.min(this.duracao(), Math.max(0, audio.currentTime + passo));
    this.atual.set(audio.currentTime);
  }

  private irParaPonto(event: PointerEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const proporcao = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const audio = this.audioRef.nativeElement;
    audio.currentTime = proporcao * this.duracao();
    this.atual.set(audio.currentTime);
  }

  aoCarregarMetadados(): void {
    const audio = this.audioRef.nativeElement;
    if (Number.isFinite(audio.duration)) {
      this.duracao.set(audio.duration);
      return;
    }    this.corrigindoDuracao = true;
    audio.currentTime = 1e101;
  }

  aoMudarDuracao(): void {
    const audio = this.audioRef.nativeElement;
    if (!Number.isFinite(audio.duration)) return;
    this.duracao.set(audio.duration);
    if (this.corrigindoDuracao) {
      this.corrigindoDuracao = false;
      audio.currentTime = 0;
      this.atual.set(0);
    }
  }

  aoAtualizarTempo(): void {
    const audio = this.audioRef.nativeElement;
    if (this.corrigindoDuracao || this.arrastando()) return;
    this.atual.set(audio.currentTime);
    if (!this.duracao() && Number.isFinite(audio.duration)) this.duracao.set(audio.duration);
  }

  aoTerminar(): void {
    this.pararAcompanhamento();
    this.tocando.set(false);
    this.atual.set(0);
    this.terminou.emit();
  }

  preenchimentoBarra(indice: number): number {
    const total = this.barras.length || 1;
    return Math.min(1, Math.max(0, this.progresso() * total - indice));
  }

  pausarReproducao(): void {
    this.pausar();
  }

  private pausar(): void {
    this.audioRef?.nativeElement.pause();
    this.tocando.set(false);
  }

  private gerarBarras(semente: string): number[] {
    let hash = 0;
    for (let i = 0; i < semente.length; i++) hash = (hash * 31 + semente.charCodeAt(i)) | 0;
    return Array.from({ length: QTD_BARRAS_PLAYER_AUDIO }, (_, i) => {
      hash = (hash * 1103515245 + 12345) | 0;
      const ruido = ((hash >>> 16) & 0xff) / 255;
      const envelope = 0.55 + 0.45 * Math.sin((i / QTD_BARRAS_PLAYER_AUDIO) * Math.PI);
      return Math.max(0.18, Math.min(1, ruido * envelope + 0.12));
    });
  }
}
